import type {
  Bounds,
  DimKind,
  GeomEntity,
  Geometry,
  Vec2,
} from '../shared/types';
import { traitsOf } from '../shared/categories';

/** Fill colour that tags a feature block (sauna heater / steam cube) so the
 *  renderer draws it as a dark solid instead of a bench. */
export const FEATURE_COLOR = '#3c3d42';

/** Default guard-wall thickness (mm) for a heater cover side. */
export const HEATER_COVER_DEFAULT = 38;
/** Wood colour of the heater guard walls. */
export const HEATER_COVER_COLOR = '#f5dbb4';
/** Default fill colour of a heater body (also the shared "Heater" named colour). */
export const HEATER_BODY_COLOR = '#4b4b4b';
/** Default clearance gap (mm) on each side of a heater body. */
export const HEATER_GAP_DEFAULT = 80;

export interface HeaterCover { front?: number; back?: number; left?: number; right?: number }
export interface HeaterGap { front?: number; back?: number; left?: number; right?: number }

const NO_GAP: HeaterGap = { front: 0, back: 0, left: 0, right: 0 };

/** The clearance gap and the guard cover a part is drawn, carved and snapped
 *  with: a `guarded` category's own (a heater's: its gap 80 a side unless it
 *  says otherwise). Nothing else has either — an infrared panel's or a shower's
 *  stored ones are ignored, and kept. The ONE reader: plan, section, 3D, bench
 *  carving and the cover's hover all ask it. */
export function heaterGuard(inst: { category: string; heaterGap?: HeaterGap; heaterCover?: HeaterCover }): { gap: HeaterGap; cover: HeaterCover | undefined } {
  return traitsOf(inst.category).guarded ? { gap: inst.heaterGap ?? {}, cover: inst.heaterCover } : { gap: NO_GAP, cover: undefined };
}

/** Resolve a heater's per-side clearance gaps (mm), defaulting absent sides to 80. */
export function heaterGapSides(gap: HeaterGap | undefined): { front: number; back: number; left: number; right: number } {
  return {
    front: gap?.front ?? HEATER_GAP_DEFAULT,
    back: gap?.back ?? HEATER_GAP_DEFAULT,
    left: gap?.left ?? HEATER_GAP_DEFAULT,
    right: gap?.right ?? HEATER_GAP_DEFAULT,
  };
}

/** Local-frame box (mm) of a heater body grown by its clearance gap on each side
 *  (front = −y, back = +y, left = −x, right = +x). */
export function heaterGapBox(gap: HeaterGap | undefined, width: number, depth: number): { x0: number; y0: number; x1: number; y1: number } {
  const s = heaterGapSides(gap);
  const hw = width / 2, hd = depth / 2;
  return { x0: -hw - s.left, x1: hw + s.right, y0: -hd - s.front, y1: hd + s.back };
}

/** The overall (local-frame) box of a heater: the union of its body, clearance
 *  gap and guard cover — the object's largest bounding box. */
export function heaterOuterCorners(
  width: number,
  depth: number,
  gap: HeaterGap | undefined,
  cover: HeaterCover | undefined,
): Vec2[] {
  const g = heaterGapBox(gap, width, depth);
  const c = heaterCoverOuterCorners(cover, width, depth, gap); // sits outside the gap; falls back to the gap box when no cover
  let x0 = g.x0, x1 = g.x1, y0 = g.y0, y1 = g.y1;
  for (const p of c) {
    if (p.x < x0) x0 = p.x;
    if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.y > y1) y1 = p.y;
  }
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
}

/** A part's overall box in its own frame: a guarded part's (a heater's) body,
 *  air gap and cover — the cover where it has one —; anything else's own
 *  footprint. */
export function partOuterCorners(inst: { category: string; width: number; depth: number; heaterGap?: HeaterGap; heaterCover?: HeaterCover }): Vec2[] {
  const g = heaterGuard(inst);
  return heaterOuterCorners(inst.width, inst.depth, g.gap, g.cover);
}

/** Centred half-extents (mm) of a heater's overall box, for hit-testing that
 *  assumes a symmetric footprint around the instance centre. */
export function heaterOuterHalfExtents(width: number, depth: number, gap: HeaterGap | undefined, cover: HeaterCover | undefined): { hw: number; hd: number } {
  const c = heaterOuterCorners(width, depth, gap, cover);
  let hw = 0, hd = 0;
  for (const p of c) { hw = Math.max(hw, Math.abs(p.x)); hd = Math.max(hd, Math.abs(p.y)); }
  return { hw, hd };
}

/** Whether an instance is drawn as a parametric heater block (body + clearance
 *  gap + guard cover). A `heaterBlock` category's parts (heaters and shower
 *  parts) are, UNLESS they carry a loaded drawing (vector geometry or raster
 *  image) — the loaded drawing wins and the block is not drawn. */
export function drawsHeaterBlock(inst: { category: string; geometry?: unknown; imageDataUrl?: string }): boolean {
  return isHeaterBlock(inst.category, !!inst.geometry || !!inst.imageDataUrl);
}

/** The same rule told whether the part carries a drawing of its own (`drawn`),
 *  for a reader that may not read one (a section: partLook.ts `solidLook`). */
export function isHeaterBlock(category: string, drawn: boolean): boolean {
  return traitsOf(category).heaterBlock && !drawn;
}

/**
 * Guard-wall band boxes (local frame, mm) around a width×depth heater, one per
 * enabled side. Bands sit OUTSIDE the clearance gap (so the order from the centre
 * out is body → air gap → cover) — front = −y, back = +y, left = −x, right = +x —
 * and overlap at corners so adjacent sides join seamlessly. The caller unions
 * them (nonzero fill + boundary-edge stroke / extrusion).
 */
export function heaterCoverBands(cover: HeaterCover | undefined, width: number, depth: number, gap?: HeaterGap): Array<{ x0: number; y0: number; x1: number; y1: number; side: HeaterSide }> {
  if (!cover) return [];
  const g = heaterGapBox(gap, width, depth);
  const tF = cover.front && cover.front > 0 ? cover.front : 0;
  const tB = cover.back && cover.back > 0 ? cover.back : 0;
  const tL = cover.left && cover.left > 0 ? cover.left : 0;
  const tR = cover.right && cover.right > 0 ? cover.right : 0;
  const oL = g.x0 - tL, oR = g.x1 + tR, oB = g.y0 - tF, oF = g.y1 + tB;
  const out: Array<{ x0: number; y0: number; x1: number; y1: number; side: HeaterSide }> = [];
  if (tF) out.push({ x0: oL, y0: oB, x1: oR, y1: g.y0, side: 'front' }); // front (−y)
  if (tB) out.push({ x0: oL, y0: g.y1, x1: oR, y1: oF, side: 'back' }); // back (+y)
  if (tL) out.push({ x0: oL, y0: oB, x1: g.x0, y1: oF, side: 'left' }); // left (−x)
  if (tR) out.push({ x0: g.x1, y0: oB, x1: oR, y1: oF, side: 'right' }); // right (+x)
  return out;
}

export type HeaterSide = 'front' | 'back' | 'left' | 'right';
export const HEATER_SIDES: HeaterSide[] = ['front', 'back', 'left', 'right'];

/** The clearance gap box's outer edge on one side, as a local-frame segment.
 *  This is the line the guard cover on that side would sit against. */
export function heaterGapEdge(
  side: HeaterSide, width: number, depth: number, gap: HeaterGap | undefined,
): [Vec2, Vec2] {
  const g = heaterGapBox(gap, width, depth);
  if (side === 'left') return [{ x: g.x0, y: g.y0 }, { x: g.x0, y: g.y1 }];
  if (side === 'right') return [{ x: g.x1, y: g.y0 }, { x: g.x1, y: g.y1 }];
  if (side === 'front') return [{ x: g.x0, y: g.y0 }, { x: g.x1, y: g.y0 }];
  return [{ x: g.x0, y: g.y1 }, { x: g.x1, y: g.y1 }];
}

/**
 * What a point over a heater offers, in its local frame: an existing guard-cover
 * band (`remove` — click clears that side) or the clearance gap's outer edge on a
 * side that has no guard yet (`add` — click builds one there). `tol` is the pick
 * tolerance in mm for the edge; the bands are picked by containment, so a corner
 * where two bands overlap goes to the side the point is furthest outside on.
 *
 * A covered side never offers `add`: there is nothing to add, and the cover's
 * inner face is exactly that gap edge, so both would answer for the same pixels.
 */
export function heaterCoverHit(
  p: Vec2, width: number, depth: number, gap: HeaterGap | undefined, cover: HeaterCover | undefined, tol: number,
): { side: HeaterSide; kind: 'add' | 'remove' } | null {
  const g = heaterGapBox(gap, width, depth);
  const outside: Record<HeaterSide, number> = {
    left: g.x0 - p.x, right: p.x - g.x1, front: g.y0 - p.y, back: p.y - g.y1,
  };
  let band: { side: HeaterSide } | null = null;
  for (const b of heaterCoverBands(cover, width, depth, gap)) {
    if (p.x < b.x0 || p.x > b.x1 || p.y < b.y0 || p.y > b.y1) continue;
    if (!band || outside[b.side] > outside[band.side]) band = b;
  }
  if (band) return { side: band.side, kind: 'remove' };
  let near: HeaterSide | null = null, best = tol;
  for (const s of HEATER_SIDES) {
    const t = cover?.[s];
    if (t && t > 0) continue;
    const [a, b] = heaterGapEdge(s, width, depth, gap);
    const d = distToSegment(p, a, b);
    if (d <= best) { best = d; near = s; }
  }
  return near ? { side: near, kind: 'add' } : null;
}

/** The 4 outer corners (local frame) of a heater grown by its cover, which sits
 *  outside the clearance gap — for bounds and bench-carving. Falls back to the
 *  gap box when there's no cover. */
export function heaterCoverOuterCorners(cover: HeaterCover | undefined, width: number, depth: number, gap?: HeaterGap): Vec2[] {
  const g = heaterGapBox(gap, width, depth);
  const oL = g.x0 - (cover?.left && cover.left > 0 ? cover.left : 0);
  const oR = g.x1 + (cover?.right && cover.right > 0 ? cover.right : 0);
  const oB = g.y0 - (cover?.front && cover.front > 0 ? cover.front : 0);
  const oF = g.y1 + (cover?.back && cover.back > 0 ? cover.back : 0);
  return [{ x: oL, y: oB }, { x: oR, y: oB }, { x: oR, y: oF }, { x: oL, y: oF }];
}

export function emptyBounds(): Bounds {
  return {
    min: { x: Infinity, y: Infinity },
    max: { x: -Infinity, y: -Infinity },
  };
}

export function isFiniteBounds(b: Bounds): boolean {
  return (
    Number.isFinite(b.min.x) &&
    Number.isFinite(b.min.y) &&
    Number.isFinite(b.max.x) &&
    Number.isFinite(b.max.y) &&
    b.max.x > b.min.x &&
    b.max.y > b.min.y
  );
}

export function expandBounds(b: Bounds, p: Vec2): void {
  if (p.x < b.min.x) b.min.x = p.x;
  if (p.y < b.min.y) b.min.y = p.y;
  if (p.x > b.max.x) b.max.x = p.x;
  if (p.y > b.max.y) b.max.y = p.y;
}

/** Compute the bounding box of a set of normalized entities. */
export function computeBounds(entities: GeomEntity[]): Bounds {
  const b = emptyBounds();
  for (const e of entities) {
    switch (e.kind) {
      case 'line':
        expandBounds(b, e.a);
        expandBounds(b, e.b);
        break;
      case 'polyline':
        for (const p of e.points) expandBounds(b, p);
        break;
      case 'circle':
      case 'arc':
        expandBounds(b, { x: e.center.x - e.radius, y: e.center.y - e.radius });
        expandBounds(b, { x: e.center.x + e.radius, y: e.center.y + e.radius });
        break;
      case 'ellipse': {
        // Axis-aligned bbox of the (full) ellipse from its major/minor axes.
        const ex = Math.hypot(e.major.x, e.major.y * e.ratio);
        const ey = Math.hypot(e.major.y, e.major.x * e.ratio);
        expandBounds(b, { x: e.center.x - ex, y: e.center.y - ey });
        expandBounds(b, { x: e.center.x + ex, y: e.center.y + ey });
        break;
      }
      case 'spline':
        // The curve lies within its control polygon's convex hull (a superset).
        for (const p of e.control) expandBounds(b, p);
        break;
      case 'text':
        expandBounds(b, e.pos);
        break;
      case 'hatch':
        for (const loop of e.loops) for (const p of loop) expandBounds(b, p);
        break;
    }
  }
  return b;
}

export function boundsCenter(b: Bounds): Vec2 {
  return { x: (b.min.x + b.max.x) / 2, y: (b.min.y + b.max.y) / 2 };
}

export function boundsSize(b: Bounds): Vec2 {
  return { x: b.max.x - b.min.x, y: b.max.y - b.min.y };
}

/** A text's justification, carried by a copy only where it has one. */
function textJustify(e: { align?: 'center' | 'right'; baseline?: 'middle' | 'top' }): { align?: 'center' | 'right'; baseline?: 'middle' | 'top' } {
  return { ...(e.align ? { align: e.align } : {}), ...(e.baseline ? { baseline: e.baseline } : {}) };
}

/** Translate every entity by (dx, dy), returning a new geometry. */
export function translateGeometry(geom: Geometry, dx: number, dy: number): Geometry {
  const t = (p: Vec2): Vec2 => ({ x: p.x + dx, y: p.y + dy });
  const entities: GeomEntity[] = geom.entities.map((e) => {
    switch (e.kind) {
      case 'line':
        return { kind: 'line', a: t(e.a), b: t(e.b), color: e.color };
      case 'polyline':
        return { kind: 'polyline', points: e.points.map(t), closed: e.closed, color: e.color };
      case 'circle':
        return { kind: 'circle', center: t(e.center), radius: e.radius, color: e.color };
      case 'arc':
        return {
          kind: 'arc',
          center: t(e.center),
          radius: e.radius,
          startAngle: e.startAngle,
          endAngle: e.endAngle,
          color: e.color,
        };
      case 'ellipse':
        // `major` is a vector, so translation leaves it unchanged.
        return { kind: 'ellipse', center: t(e.center), major: e.major, ratio: e.ratio, start: e.start, end: e.end, closed: e.closed, color: e.color };
      case 'spline':
        return { kind: 'spline', control: e.control.map(t), degree: e.degree, knots: e.knots, closed: e.closed, color: e.color };
      case 'text':
        return { kind: 'text', pos: t(e.pos), height: e.height, value: e.value, ...textJustify(e), color: e.color };
      case 'hatch':
        return { kind: 'hatch', loops: e.loops.map((l) => l.map(t)), solid: e.solid, color: e.color };
    }
  });
  return { entities, bounds: computeBounds(entities) };
}

/** Re-centre a geometry so its bounding-box centre is at the local origin. */
export function centerGeometry(geom: Geometry): Geometry {
  const c = boundsCenter(geom.bounds);
  return translateGeometry(geom, -c.x, -c.y);
}

/** Scale every entity about the local origin by (fx, fy). */
export function scaleGeometry(geom: Geometry, fx: number, fy: number): Geometry {
  const s = (p: Vec2): Vec2 => ({ x: p.x * fx, y: p.y * fy });
  const rScale = (Math.abs(fx) + Math.abs(fy)) / 2;
  const entities: GeomEntity[] = geom.entities.map((e) => {
    switch (e.kind) {
      case 'line':
        return { kind: 'line', a: s(e.a), b: s(e.b), color: e.color };
      case 'polyline':
        return { kind: 'polyline', points: e.points.map(s), closed: e.closed, color: e.color };
      case 'circle':
        return { kind: 'circle', center: s(e.center), radius: e.radius * rScale, color: e.color };
      case 'arc':
        return {
          kind: 'arc',
          center: s(e.center),
          radius: e.radius * rScale,
          startAngle: e.startAngle,
          endAngle: e.endAngle,
          color: e.color,
        };
      case 'ellipse':
        // Scale the centre and major-axis vector; ratio holds under uniform scale.
        return { kind: 'ellipse', center: s(e.center), major: { x: e.major.x * fx, y: e.major.y * fy }, ratio: e.ratio, start: e.start, end: e.end, closed: e.closed, color: e.color };
      case 'spline':
        return { kind: 'spline', control: e.control.map(s), degree: e.degree, knots: e.knots, closed: e.closed, color: e.color };
      case 'text':
        return { kind: 'text', pos: s(e.pos), height: e.height * fy, value: e.value, ...textJustify(e), color: e.color };
      case 'hatch':
        return { kind: 'hatch', loops: e.loops.map((l) => l.map(s)), solid: e.solid, color: e.color };
    }
  });
  return { entities, bounds: computeBounds(entities) };
}

export function degToRad(d: number): number {
  return (d * Math.PI) / 180;
}

export function addVec(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

/** Per-axis scale-factor bounds that keep a ring's length-locked edges from
 *  stretching under a bounding-box resize: a locked edge freezes its dominant
 *  axis (a horizontal lock pins the X scale to 1, a vertical lock pins Y), the
 *  same way `roomResizeConstraints` freezes a locked wall's axis. Edges with no
 *  length lock leave both axes free. */
export function ringScaleBounds(pts: Vec2[], edgeLengths?: Array<number | null>): { loX: number; hiX: number; loY: number; hiY: number } {
  const b = { loX: 0, hiX: Infinity, loY: 0, hiY: Infinity };
  if (!edgeLengths) return b;
  const n = pts.length;
  for (let i = 0; i < n && i < edgeLengths.length; i++) {
    if ((edgeLengths[i] ?? null) == null) continue;
    const a = pts[i], c = pts[(i + 1) % n];
    const dx = c.x - a.x, dy = c.y - a.y;
    if (Math.hypot(dx, dy) < 1e-6) continue;
    if (Math.abs(dx) >= Math.abs(dy)) { b.loX = 1; b.hiX = 1; } // horizontal lock → freeze X
    else { b.loY = 1; b.hiY = 1; }                              // vertical lock → freeze Y
  }
  return b;
}

export function subVec(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Rotate a point about the origin by `deg` degrees (CCW). */
export function rotatePoint(p: Vec2, deg: number): Vec2 {
  const a = degToRad(deg);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return { x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos };
}

/** What places a local frame in the world: its origin and its rotation (CCW °). */
export interface Placed { x: number; y: number; rotation: number }

/** A point in a placed part's local frame, in world coordinates. */
export function instanceToWorld(inst: Placed, p: Vec2): Vec2 {
  const r = rotatePoint(p, inst.rotation);
  return { x: r.x + inst.x, y: r.y + inst.y };
}

/** A world point in a placed part's local frame: instanceToWorld undone. */
export function toInstanceLocal(inst: Placed, world: Vec2): Vec2 {
  return rotatePoint({ x: world.x - inst.x, y: world.y - inst.y }, -inst.rotation);
}

/** One shallow, outward-bulging arc for a revision cloud, replacing a
 *  straight chord from `p0` to `p1`. Assumes `p0`/`p1` come from a
 *  CCW-wound outline (see `ensureCcw`): the outward normal is then the chord
 *  direction rotated -90°, and the arc sweeps p0→p1 going counter-clockwise
 *  (matching `addArcPath`'s sweep convention), so a chain of these for
 *  consecutive edges forms one continuous scalloped path. Returns null for a
 *  degenerate (near-zero-length) chord. */
export interface BulgeArc { center: Vec2; radius: number; startDeg: number; endDeg: number }
export function bulgeArc(p0: Vec2, p1: Vec2, sagitta: number): BulgeArc | null {
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  const c = Math.hypot(dx, dy);
  if (c < 1e-6) return null;
  const h = Math.min(sagitta, c / 2);
  if (h <= 0) return null;
  const nx = dy / c, ny = -dx / c; // outward normal, for a CCW-wound outline
  const mx = (p0.x + p1.x) / 2, my = (p0.y + p1.y) / 2;
  const r = h / 2 + (c * c) / (8 * h);
  const d = r - h; // centre offset from the chord midpoint, toward the inside
  const cx = mx - nx * d, cy = my - ny * d;
  const a0 = (Math.atan2(p0.y - cy, p0.x - cx) * 180) / Math.PI;
  const a1 = (Math.atan2(p1.y - cy, p1.x - cx) * 180) / Math.PI;
  return { center: { x: cx, y: cy }, radius: r, startDeg: a0, endDeg: a1 };
}

/** `points` wound counter-clockwise (standard math orientation), reversing it
 *  if it's currently clockwise. Required before generating outward-bulging
 *  scallops with `bulgeArc`, which assumes CCW winding. */
export function ensureCcw(points: Vec2[]): Vec2[] {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i], b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area < 0 ? [...points].reverse() : points;
}

/** A revision cloud's sagitta, as a fraction of each scallop's chord. */
const REVCLOUD_BULGE_RATIO = 0.3;
/** A revision cloud's scallop chord in model space, where no screen sets it
 *  (the DXF): 4 mm on a sheet at 1:50, the default viewport's scale. */
export const REVCLOUD_SCALLOP_MM = 200;

/** A revision cloud's scallops, in order round its closed outline (wound CCW):
 *  each edge cut into equal chords of about `scallopMm` (at least one), each
 *  chord an outward-bulging arc. The ONE rule the canvas (`scallopMm` from its
 *  zoom) and the DXF (`REVCLOUD_SCALLOP_MM`) draw. */
export function cloudScallops(points: readonly Vec2[], scallopMm: number): BulgeArc[] {
  const pts = ensureCcw([...points]);
  const out: BulgeArc[] = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const p0 = pts[i], p1 = pts[(i + 1) % n];
    const edgeLen = distance(p0, p1);
    if (edgeLen < 1e-6) continue;
    const segs = Math.max(1, Math.round(edgeLen / scallopMm));
    for (let s = 0; s < segs; s++) {
      const t0 = s / segs, t1 = (s + 1) / segs;
      const a: Vec2 = { x: p0.x + (p1.x - p0.x) * t0, y: p0.y + (p1.y - p0.y) * t0 };
      const b: Vec2 = { x: p0.x + (p1.x - p0.x) * t1, y: p0.y + (p1.y - p0.y) * t1 };
      const arc = bulgeArc(a, b, distance(a, b) * REVCLOUD_BULGE_RATIO);
      if (arc) out.push(arc);
    }
  }
  return out;
}

/**
 * Zoom-adaptive editing granularity in mm. Dragging (moves, resizes, vertices)
 * snaps to this step, and the background grid is drawn at it. The step is the
 * finest of 1 / 10 / 50 / 100 mm whose on-screen spacing stays legible (≥ ~8 px),
 * so you get 1 mm control when zoomed in and 100 mm when zoomed far out.
 */
export function gridStepMm(scale: number): number {
  for (const step of [1, 10, 50, 100]) {
    if (step * scale >= 8) return step;
  }
  return 100;
}

// ---- Shared geometry primitives (used by snapping, offset, trim, fillet) ----

/** A rounded corner: the arc that replaces the sharp vertex `C` between the two
 *  segments C→prev and C→next. `station` is the distance from `C` along EACH
 *  adjacent edge at which the arc starts — the key property being that every
 *  face concentric with this corner (the wall's inner face, each bench ring)
 *  shares that same station, because the offset lines are parallel and the arc
 *  centre is common. A face inset by `d` therefore has radius `radius - d` and
 *  keeps the same tangent stations; once `radius - d <= 0` that face is sharp.
 *  At a REFLEX corner the centre lies outside the room and the inset face is
 *  `radius + d` (polyRoom's `faceRadius`). */
export interface CornerFillet {
  /** The angle between the two edges (radians, ≤ π): the interior angle at a
   *  convex corner, 2π − interior at a reflex one. */
  theta: number;
  /** Arc radius on the reference (outermost) face. */
  radius: number;
  /** Distance from the corner vertex along each adjacent edge to the tangent point. */
  station: number;
  /** Arc centre — shared by every concentric face. */
  center: Vec2;
  /** Tangent point on the C→prev edge. */
  t1: Vec2;
  /** Tangent point on the C→next edge. */
  t2: Vec2;
}

/** Round the corner at `C` between neighbours `prev` and `next` with the given
 *  outer-face radius. Null when the corner is degenerate (collinear or zero
 *  radius), in which case the corner stays sharp. */
export function cornerFillet(prev: Vec2, C: Vec2, next: Vec2, radius: number): CornerFillet | null {
  if (!(radius > 0)) return null;
  const n1 = normalize({ x: prev.x - C.x, y: prev.y - C.y });
  const n2 = normalize({ x: next.x - C.x, y: next.y - C.y });
  if (!n1 || !n2) return null;
  const theta = Math.acos(Math.max(-1, Math.min(1, n1.x * n2.x + n1.y * n2.y)));
  if (theta < 1e-3 || Math.PI - theta < 1e-3) return null; // collinear: nothing to round
  const station = radius / Math.tan(theta / 2);
  const bis = normalize({ x: n1.x + n2.x, y: n1.y + n2.y });
  if (!bis) return null;
  const off = radius / Math.sin(theta / 2);
  return {
    theta,
    radius,
    station,
    center: { x: C.x + bis.x * off, y: C.y + bis.y * off },
    t1: { x: C.x + n1.x * station, y: C.y + n1.y * station },
    t2: { x: C.x + n2.x * station, y: C.y + n2.y * station },
  };
}

/** Points along the minor arc from `from` to `to` about `center`, inclusive of
 *  both ends. Segment count follows a max sag of ~1.5 mm so the tessellation is
 *  fine on a big radius and cheap on a small one. */
export function arcPoints(center: Vec2, from: Vec2, to: Vec2, radius: number): Vec2[] {
  if (!(radius > 0)) return [from, to];
  const a0 = Math.atan2(from.y - center.y, from.x - center.x);
  let sweep = Math.atan2(to.y - center.y, to.x - center.x) - a0;
  while (sweep <= -Math.PI) sweep += 2 * Math.PI;
  while (sweep > Math.PI) sweep -= 2 * Math.PI;
  const maxStep = 2 * Math.acos(Math.max(0, Math.min(1, 1 - 1.5 / radius)));
  const steps = Math.max(2, Math.min(96, Math.ceil(Math.abs(sweep) / Math.max(1e-3, maxStep))));
  const out: Vec2[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 + (sweep * i) / steps;
    out.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
  }
  return out;
}

function normalize(v: Vec2): Vec2 | null {
  const L = Math.hypot(v.x, v.y);
  return L < 1e-9 ? null : { x: v.x / L, y: v.y / L };
}

/** Foot of the perpendicular from `p` onto the segment a→b, clamped to the
 *  segment ends. Returns `a` for a degenerate (zero-length) segment. */
export function projectToSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

/** The fraction (0..1 of total arc length) of the point on polyline `path`
 *  nearest to `p`. Degenerate paths return 0. The inverse of {@link pointAtFraction}
 *  — used to drag a badge along a route and store its position as a percentage. */
export function nearestFractionOnPath(path: Vec2[], p: Vec2): number {
  if (path.length < 2) return 0;
  const seg: number[] = [];
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) { const d = distance(path[i], path[i + 1]); seg.push(d); total += d; }
  if (total === 0) return 0;
  let best = Infinity, bestArc = 0, acc = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const foot = projectToSegment(p, path[i], path[i + 1]);
    const d = distance(p, foot);
    if (d < best) { best = d; bestArc = acc + distance(path[i], foot); }
    acc += seg[i];
  }
  return Math.max(0, Math.min(1, bestArc / total));
}

/** The point at fraction `t` (0..1) of `path`'s total arc length. `t<=0` gives the
 *  first vertex, `t>=1` the last. The inverse of {@link nearestFractionOnPath}. */
export function pointAtFraction(path: Vec2[], t: number): Vec2 {
  if (!path.length) return { x: 0, y: 0 };
  if (path.length === 1) return { ...path[0] };
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) total += distance(path[i], path[i + 1]);
  let want = total * Math.max(0, Math.min(1, t));
  for (let i = 0; i < path.length - 1; i++) {
    const d = distance(path[i], path[i + 1]);
    if (want <= d || i === path.length - 2) {
      const f = d ? want / d : 0;
      return { x: path[i].x + (path[i + 1].x - path[i].x) * f, y: path[i].y + (path[i + 1].y - path[i].y) * f };
    }
    want -= d;
  }
  return { ...path[path.length - 1] };
}

/** Foot of the perpendicular from `p` onto the *infinite* line through a→b
 *  (unclamped). Returns `a` for a degenerate segment. */
export function perpFoot(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return { x: a.x, y: a.y };
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  return { x: a.x + t * dx, y: a.y + t * dy };
}

/** Lock the segment prev→p to the nearest `stepDeg`-degree multiple direction,
 *  keeping the cursor's distance from `prev` (so a 45°/90° run stays exact). */
export function applyAngleLock(prev: Vec2 | undefined, p: Vec2, on: boolean, stepDeg: number): Vec2 {
  if (!on || !prev) return p;
  const dx = p.x - prev.x, dy = p.y - prev.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-6) return p;
  const step = (stepDeg * Math.PI) / 180;
  const ang = Math.round(Math.atan2(dy, dx) / step) * step;
  return { x: prev.x + Math.cos(ang) * dist, y: prev.y + Math.sin(ang) * dist };
}

/** The turn (degrees CCW) that makes the line `pivot`→`p` horizontal, the shorter
 *  way round: in [−90, 90) — a vertical line turns clockwise. Null when `p` is on
 *  the pivot. */
export function levelTurnDeg(pivot: Vec2, p: Vec2): number | null {
  const dx = p.x - pivot.x, dy = p.y - pivot.y;
  if (Math.hypot(dx, dy) < 1e-6) return null;
  let turn = -(Math.atan2(dy, dx) * 180) / Math.PI;
  while (turn >= 90) turn -= 180;
  while (turn < -90) turn += 180;
  return turn;
}

/** Context segment for a draft/ring: the last two points, or a degenerate
 *  `[v, v]` for a single point (so a 45° ray still anchors to it). */
export function draftSeg(pts: Vec2[]): readonly [Vec2, Vec2] | undefined {
  const n = pts.length;
  if (n >= 2) return [pts[n - 2], pts[n - 1]];
  if (n === 1) return [pts[0], pts[0]];
  return undefined;
}

export interface DimGeom {
  /** Direction the dimension line runs. */
  u: Vec2;
  /** The axis `offset` is measured along (u rotated +90°). */
  n: Vec2;
  /** The dimension line's two ends — the measured points projected onto it. */
  a: Vec2;
  b: Vec2;
  /** The figure the label reads: the span between those two ends. */
  length: number;
}

/** The direction a dimension of each kind runs, and its offset axis. */
function dimAxes(a: Vec2, b: Vec2, kind: DimKind): { u: Vec2; n: Vec2 } | null {
  if (kind === 'horizontal') return { u: { x: 1, y: 0 }, n: { x: 0, y: 1 } };
  if (kind === 'vertical') return { u: { x: 0, y: 1 }, n: { x: -1, y: 0 } };
  const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy);
  if (L < 1e-9) return null;
  return { u: { x: dx / L, y: dy / L }, n: { x: -dy / L, y: dx / L } };
}

/**
 * Where a dimension is drawn, and what it reads — the ONE resolver every consumer
 * goes through (the renderer, the backdrop-sample box, the hit test), so none of
 * them can disagree about the line's position or its figure.
 *
 * The line runs along `u` through `mid(a, b) + n · offset`, and its ends are the
 * two measured points PROJECTED onto it; the extension lines close the gap from
 * each point to its projection. For an `aligned` dimension that is identical to
 * the old `a + n·offset → b + n·offset`, because `a` and `b` project straight
 * back — which is why the stored `offset` means the same thing as it always did
 * and no saved drawing shifts.
 */
export function dimensionGeometry(a: Vec2, b: Vec2, offset: number, kind: DimKind = 'aligned'): DimGeom | null {
  const ax = dimAxes(a, b, kind);
  if (!ax) return null;
  const { u, n } = ax;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const p = { x: mid.x + n.x * offset, y: mid.y + n.y * offset };
  const along = (q: Vec2): Vec2 => {
    const t = (q.x - p.x) * u.x + (q.y - p.y) * u.y;
    return { x: p.x + u.x * t, y: p.y + u.y * t };
  };
  return { u, n, a: along(a), b: along(b), length: Math.abs((b.x - a.x) * u.x + (b.y - a.y) * u.y) };
}

/** The `offset` that puts a dimension of `kind` through `p`. */
export function dimensionOffset(a: Vec2, b: Vec2, p: Vec2, kind: DimKind = 'aligned'): number {
  const ax = dimAxes(a, b, kind);
  if (!ax) return 0;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  return (p.x - mid.x) * ax.n.x + (p.y - mid.y) * ax.n.y;
}

/**
 * Which KIND of dimension the offset pick is asking for, from where the cursor
 * sits relative to the two measured points — AutoCAD's `DIM`: drag above or below
 * the span and you are placing a horizontal dimension, drag out to the side and
 * it is a vertical one, drag perpendicular to a slanted span and it is aligned.
 *
 * Each kind offers its offset AXIS, and the nearest of those to the cursor
 * direction wins (undirected, since `offset` is signed and either side is the
 * same kind). A kind whose span would collapse is not offered at all: a
 * horizontal dimension across a vertical pair measures nothing, so dragging
 * sideways there falls back to aligned rather than producing a zero figure.
 * `aligned` wins a tie, so an axis-parallel pair keeps behaving exactly as it did
 * before kinds existed.
 */
export function inferDimKind(a: Vec2, b: Vec2, p: Vec2, minSpan = 1e-6): DimKind {
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const vx = p.x - mid.x, vy = p.y - mid.y;
  const vl = Math.hypot(vx, vy);
  if (vl < 1e-9) return 'aligned';
  let best: DimKind = 'aligned';
  let bestScore = -1;
  for (const kind of ['aligned', 'horizontal', 'vertical'] as const) {
    const ax = dimAxes(a, b, kind);
    if (!ax) continue;
    if (Math.abs((b.x - a.x) * ax.u.x + (b.y - a.y) * ax.u.y) < minSpan) continue;
    const score = Math.abs((vx * ax.n.x + vy * ax.n.y) / vl); // |cos| to the offset axis
    if (score > bestScore + 1e-9) { bestScore = score; best = kind; }
  }
  return best;
}

/** The shape `pickSnapCandidate` ranks: a point, its OSNAP kind, and whether it
 *  carries an anchor (which only breaks a tie between equals). */
export interface RankedSnap {
  p: Vec2;
  kind?: string;
  anchor?: unknown;
}

/**
 * Pick the object snap a cursor has acquired: AutoCAD's aperture rule, pure and
 * unit-tested because it decides where every click lands.
 *
 * The aperture is the whole rule. Everything farther than `tol` is out of reach;
 * among what is left, the **highest OSNAP `priority` wins outright** (endpoint >
 * node > intersection > midpoint > centre > quadrant > perpendicular > nearest),
 * and distance only separates two candidates of the same precedence. An anchored
 * candidate comes first among those, so a dimension keeps tracking the vertex it
 * was taken from. So a corner anywhere inside the aperture beats a midpoint or a
 * perpendicular foot sitting right under the cursor — which is the point: the
 * corner is what you are aiming at, and the aperture is how close you have to be
 * to claim it.
 *
 * That makes the order a strict total order — precedence, then distance, then
 * anchor — so the winner cannot depend on the sequence candidates arrive in.
 * A near-tie BAND was tried instead and is the trap to stay out of: "within
 * `tieEps`, precedence; outside it, distance" is not transitive, so a greedy scan
 * ratchets. A perpendicular 0.5 px under the cursor lost to a midpoint 5 px out,
 * which then lost to an intersection 9 px out, landing the pick 9 px away — and
 * reversing the order of those same three gave a different winner. Emission order
 * genuinely varies (`edgeSnapCandidates` re-sorts by distance every mousemove),
 * so the pick flickered frame to frame.
 */
export function pickSnapCandidate<T extends RankedSnap>(
  world: Vec2,
  cands: Iterable<T>,
  tol: number,
  priority: Readonly<Record<string, number>>,
  enabled?: Readonly<Record<string, boolean>>,
  defaultKind = 'endpoint',
): T | null {
  let best: T | null = null;
  let bestD = Infinity;
  let bestP = -Infinity;
  for (const c of cands) {
    const kind = c.kind ?? defaultKind;
    if (enabled && !enabled[kind]) continue;
    const dd = Math.hypot(world.x - c.p.x, world.y - c.p.y);
    // Written as `!(dd <= tol)` so a NaN distance is REFUSED, not admitted:
    // `NaN > tol` is false, so the plain test let a degenerate candidate through,
    // and once one became `best` nothing of equal-or-lower precedence could ever
    // displace it (every later comparison against NaN is false too). One bad
    // point — a section whose cut resolves to nothing, a collapsed ellipse ring,
    // a malformed plan entity — therefore killed snapping for that whole kind,
    // silently and only on the drawing that contained it.
    if (!(dd <= tol)) continue;
    const p = priority[kind] ?? 0;
    const win = !best ? true
      : p !== bestP ? p > bestP
        : dd < bestD - 1e-6 ? true
          : dd <= bestD + 1e-6 && !!c.anchor && !best.anchor;
    if (win) { best = c; bestD = dd; bestP = p; }
  }
  return best;
}

export interface DraftGuideOpts {
  /** Offer a 45°-multiple ray from the last vertex. */
  allow45?: boolean;
  /** Last drawn segment: its end anchors the 45° ray and the 90° normal guide. */
  lastSeg?: readonly [Vec2, Vec2];
  /** Ortho / polar hard-lock step in degrees; 0 or absent leaves the guides free. */
  lockStep?: number;
}

/**
 * The guide half of the app's one drafting snap, in whatever units the caller
 * works in — `base` is already proximity-snapped to existing geometry and `thr`
 * is the pick tolerance in those units (typically 12 px / the view's scale).
 * It offers, and picks the nearest of:
 *   - independent horizontal/vertical alignment to `candidates` (X from one
 *     vertex, Y from another — both axes at once);
 *   - the 90° normal of the last drawn segment (`lastSeg`);
 *   - a 45°-multiple ray from the last vertex (`allow45`).
 * An ortho/polar `lockStep` overrides all of them while it is on.
 *
 * Unit-agnostic on purpose: the model line tool passes world mm, the sheet line
 * tool paper mm, and they behave identically because it is the same routine.
 */
export function snapDraftGuides(base: Vec2, candidates: Vec2[], thr: number, opts: DraftGuideOpts = {}): Vec2 {
  // Ortho / polar hard lock: constrain the new point to an allowed direction from
  // the anchor vertex, at the cursor's distance (AutoCAD ORTHO / POLAR). Ortho (90°)
  // wins over polar; the lock overrides alignment/45° guides while it's on.
  const anchor = opts.lastSeg?.[1];
  if (opts.lockStep && anchor) return applyAngleLock(anchor, base, true, opts.lockStep);
  const guides: Vec2[] = [];

  // Orthogonal projection to every candidate vertex, snapping BOTH axes at once:
  // X to the nearest vertex's column and Y to the nearest vertex's row, over ALL
  // vertices (not just the last). When two different vertices supply the two
  // axes, the point lands on the intersection of their projections (e.g. the new
  // vertex on A's horizontal line and B's vertical line). A genuine two-axis lock
  // is deliberate, so it wins outright; a single-axis projection competes with
  // the 45°/normal guides.
  let sx = base.x, sy = base.y, bx = thr, by = thr, alignX = false, alignY = false;
  for (const v of candidates) {
    const dx = Math.abs(base.x - v.x);
    if (dx < bx) { bx = dx; sx = v.x; alignX = true; }
    const dy = Math.abs(base.y - v.y);
    if (dy < by) { by = dy; sy = v.y; alignY = true; }
  }
  if (alignX && alignY) return { x: sx, y: sy }; // intersection of two projections
  if (alignX || alignY) guides.push({ x: sx, y: sy });

  const prev = opts.lastSeg?.[1];
  if (opts.lastSeg) {
    // 90° normal of the last segment (skipped when the segment is degenerate).
    const [a, b] = opts.lastSeg;
    const sdx = b.x - a.x, sdy = b.y - a.y, len = Math.hypot(sdx, sdy);
    if (len > 0) {
      const tdx = sdx / len, tdy = sdy / len, ndx = -tdy, ndy = tdx;
      if (Math.abs((base.x - b.x) * tdx + (base.y - b.y) * tdy) < thr) {
        const t = (base.x - b.x) * ndx + (base.y - b.y) * ndy;
        guides.push({ x: b.x + t * ndx, y: b.y + t * ndy });
      }
    }
  }
  if (opts.allow45 && prev) {
    // 45°-multiple ray from the last vertex, accepted only within threshold.
    const locked = applyAngleLock(prev, base, true, 45);
    if (Math.hypot(locked.x - base.x, locked.y - base.y) <= thr) guides.push(locked);
  }

  // Whichever guide the cursor is closest to; else the proximity-snapped base.
  let best = base, bestD = Infinity;
  for (const g of guides) {
    const d = Math.hypot(g.x - base.x, g.y - base.y);
    if (d < bestD) { bestD = d; best = g; }
  }
  return best;
}

/**
 * Snap points an imported plan's entities contribute, already shifted by the
 * plan's move offset — the plan is drawn through `backgroundOffset`, so points
 * read from the raw entities sit at the position it was imported at, not where
 * it now is.
 *
 * Real vertices and endpoints only: a circle has no vertex, so it offers just
 * its centre; an arc offers its centre and its two ends; an ellipse its centre
 * and the two major-axis tips; a spline its control points. `startAngle` /
 * `endAngle` are DEGREES (what `addArcPath` and the DXF export both read).
 * A hatch is deliberately skipped — its loops normally duplicate an outline
 * polyline that is already in the entity list, so they cost a per-mousemove
 * pass over thousands of points and add nothing.
 */
export function planSnapPoints(
  entities: readonly GeomEntity[],
  offset: Vec2 = { x: 0, y: 0 }
): Array<{ p: Vec2; center?: true }> {
  const out: Array<{ p: Vec2; center?: true }> = [];
  const at = (p: Vec2): void => { out.push({ p: { x: p.x + offset.x, y: p.y + offset.y } }); };
  const atCenter = (p: Vec2): void => { out.push({ p: { x: p.x + offset.x, y: p.y + offset.y }, center: true }); };
  const onCircle = (c: Vec2, r: number, deg: number): Vec2 => ({
    x: c.x + r * Math.cos((deg * Math.PI) / 180),
    y: c.y + r * Math.sin((deg * Math.PI) / 180),
  });
  for (const e of entities) {
    switch (e.kind) {
      case 'line': at(e.a); at(e.b); break;
      case 'polyline': for (const v of e.points) at(v); break;
      case 'circle': atCenter(e.center); break;
      case 'arc':
        atCenter(e.center);
        at(onCircle(e.center, e.radius, e.startAngle));
        at(onCircle(e.center, e.radius, e.endAngle));
        break;
      case 'ellipse':
        atCenter(e.center);
        at({ x: e.center.x + e.major.x, y: e.center.y + e.major.y });
        at({ x: e.center.x - e.major.x, y: e.center.y - e.major.y });
        break;
      case 'spline': for (const v of e.control) at(v); break;
      case 'text': at(e.pos); break;
      default: break; // hatch — see above
    }
  }
  return out;
}

/**
 * The imported plan's straight runs, shifted by the same `offset` as
 * `planSnapPoints` — the twin it must stay in step with. These are what the
 * DERIVED snaps (midpoint / nearest / perpendicular foot / intersection) are
 * computed from, so reading them raw while the vertices honoured the offset left
 * a moved plan snapping to nothing along its walls, and firing on empty space
 * back where it was imported.
 *
 * Lines and polylines only: the curve kinds are tessellated at draw time, and a
 * hatch is skipped for the same reason it is in `planSnapPoints`.
 */
export function planSnapEdges(
  entities: readonly GeomEntity[],
  offset: Vec2 = { x: 0, y: 0 }
): Array<[Vec2, Vec2]> {
  const out: Array<[Vec2, Vec2]> = [];
  const at = (p: Vec2): Vec2 => ({ x: p.x + offset.x, y: p.y + offset.y });
  for (const e of entities) {
    if (e.kind === 'line') out.push([at(e.a), at(e.b)]);
    else if (e.kind === 'polyline') {
      for (let i = 0; i + 1 < e.points.length; i++) out.push([at(e.points[i]), at(e.points[i + 1])]);
      if (e.closed && e.points.length > 2) out.push([at(e.points[e.points.length - 1]), at(e.points[0])]);
    }
  }
  return out;
}

/** Nearest of `candidates` within `thr`, else `p` — the proximity ("object")
 *  snap a drafting tool applies before the alignment guides. */
export function nearestVertexSnap(p: Vec2, candidates: Vec2[], thr: number): Vec2 {
  let best = p, bestD = thr;
  for (const v of candidates) {
    const d = Math.hypot(v.x - p.x, v.y - p.y);
    if (d <= bestD) { bestD = d; best = v; }
  }
  return best;
}

/**
 * The offset that lands a dragged ring's GRABBED corner on a snap target.
 *
 * Only the vertex nearest `cursor` is eligible: with every vertex competing, a
 * large shape jumps sideways whenever any far corner happens to pass near
 * something, which reads as the shape moving on its own. The corner under the
 * hand is the one the user is aiming, so it is the one that snaps.
 *
 * `moved` is the ring at its un-snapped dragged position, `targets` any other
 * object's vertices, `thr` the pick tolerance in the same unit. Null = no target
 * within reach, i.e. leave the drag alone.
 */
export function moveSnapOffset(
  moved: Vec2[], cursor: Vec2, targets: Vec2[], thr: number,
): { dx: number; dy: number; at: Vec2 } | null {
  if (!moved.length || !targets.length) return null;
  let grabbed = moved[0], gd = Infinity;
  for (const p of moved) {
    const d = Math.hypot(p.x - cursor.x, p.y - cursor.y);
    if (d < gd) { gd = d; grabbed = p; }
  }
  let best: { dx: number; dy: number; at: Vec2 } | null = null;
  let bestD = thr;
  for (const t of targets) {
    const d = Math.hypot(grabbed.x - t.x, grabbed.y - t.y);
    if (d < bestD) { bestD = d; best = { dx: t.x - grabbed.x, dy: t.y - grabbed.y, at: { x: t.x, y: t.y } }; }
  }
  return best;
}

/**
 * The vertex in `verts` nearest `p` within `thr` (world units), or null when none
 * is close enough. Used to resolve which corner the cursor is hovering, so a move
 * drag can grab THAT vertex and snap it (rather than the object's origin).
 */
export function nearestVertexWithin(p: Vec2, verts: Vec2[], thr: number): Vec2 | null {
  let best: Vec2 | null = null;
  let bd = thr;
  for (const v of verts) {
    const d = Math.hypot(v.x - p.x, v.y - p.y);
    if (d <= bd) { bd = d; best = v; }
  }
  return best ? { x: best.x, y: best.y } : null;
}

/**
 * Axis-alignment for a rigid object being MOVED (an image / PDF underlay): the
 * whole body shifts so one of its corners lines up with another object's vertex on
 * X and/or Y — Figma-style smart guides, not vertex-to-vertex coincidence.
 *
 * Each axis is resolved independently: the (corner, target) pair with the smallest
 * gap within `thr` wins that axis, and the two axes may come from different corners
 * (the left edge lines up with one object, the top with another). Returns the
 * offset to add to the body plus the guide segments to draw (each from the aligned
 * corner, AFTER the offset, to the target vertex it locked onto) — or null when
 * nothing lines up. Pure and unit-tested.
 */
export function moveAlignOffset(
  corners: Vec2[], targets: Vec2[], thr: number,
): { off: Vec2; guides: Array<{ from: Vec2; to: Vec2 }> } | null {
  if (!corners.length || !targets.length) return null;
  let bx: { gap: number; c: Vec2; t: Vec2 } | null = null;
  let by: { gap: number; c: Vec2; t: Vec2 } | null = null;
  for (const c of corners) {
    for (const t of targets) {
      const gx = Math.abs(c.x - t.x);
      if (gx <= thr && (!bx || gx < bx.gap)) bx = { gap: gx, c, t };
      const gy = Math.abs(c.y - t.y);
      if (gy <= thr && (!by || gy < by.gap)) by = { gap: gy, c, t };
    }
  }
  if (!bx && !by) return null;
  const off = { x: bx ? bx.t.x - bx.c.x : 0, y: by ? by.t.y - by.c.y : 0 };
  const guides: Array<{ from: Vec2; to: Vec2 }> = [];
  // The vertical guide runs down the shared column; the horizontal along the row.
  if (bx) guides.push({ from: { x: bx.t.x, y: bx.c.y + off.y }, to: { x: bx.t.x, y: bx.t.y } });
  if (by) guides.push({ from: { x: by.c.x + off.x, y: by.t.y }, to: { x: by.t.x, y: by.t.y } });
  return { off, guides };
}

/** Distance from `p` to the segment a→b. */
export function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  return distance(p, projectToSegment(p, a, b));
}

/** Intersection of the infinite lines (p1 + t·d1) and (p2 + s·d2); null when
 *  (near-)parallel. `d1`/`d2` are direction vectors (need not be unit). */
export function lineLineIntersect(p1: Vec2, d1: Vec2, p2: Vec2, d2: Vec2): Vec2 | null {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < 1e-9) return null;
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/** Intersection of the bounded segments a→b and c→d, or null if they don't
 *  cross within both spans. `t`/`u` are the parametric positions along each. */
export function segSegIntersect(a: Vec2, b: Vec2, c: Vec2, d: Vec2): { t: number; u: number; pt: Vec2 } | null {
  const rx = b.x - a.x, ry = b.y - a.y;
  const sx = d.x - c.x, sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denom;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return { t, u, pt: { x: a.x + t * rx, y: a.y + t * ry } };
}

/** The 0, 1 or 2 intersection points of the circle (centre `c`, radius `r`)
 *  with the *infinite* line through a→b, ordered by ascending line parameter. */
export function circleLineIntersect(c: Vec2, r: number, a: Vec2, b: Vec2): Vec2[] {
  const dx = b.x - a.x, dy = b.y - a.y;
  const A = dx * dx + dy * dy;
  if (A < 1e-12) return [];
  const fx = a.x - c.x, fy = a.y - c.y;
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - r * r;
  let disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  disc = Math.sqrt(disc);
  const t1 = (-B - disc) / (2 * A), t2 = (-B + disc) / (2 * A);
  const pts = [{ x: a.x + t1 * dx, y: a.y + t1 * dy }];
  if (Math.abs(t2 - t1) > 1e-9) pts.push({ x: a.x + t2 * dx, y: a.y + t2 * dy });
  return pts;
}

/** The circle through three points (circumcircle), or null if they're collinear. */
export function circleFrom3Points(a: Vec2, b: Vec2, c: Vec2): { center: Vec2; radius: number } | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-9) return null;
  const a2 = a.x * a.x + a.y * a.y, b2 = b.x * b.x + b.y * b.y, c2 = c.x * c.x + c.y * c.y;
  const ux = (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d;
  const uy = (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d;
  const center = { x: ux, y: uy };
  return { center, radius: distance(center, a) };
}

/** The arc from `p0` through `p1` to `p2`, as the renderer stores it (it sweeps
 *  CCW from `startDeg` to `endDeg`): the order whose CCW sweep contains `p1`, so
 *  the arc really passes through the middle point. Null when the three are
 *  collinear. */
export function arcThroughPoints(p0: Vec2, p1: Vec2, p2: Vec2): { center: Vec2; radius: number; startDeg: number; endDeg: number } | null {
  const cc = circleFrom3Points(p0, p1, p2);
  if (!cc) return null;
  const deg = (p: Vec2): number => (Math.atan2(p.y - cc.center.y, p.x - cc.center.x) * 180) / Math.PI;
  const norm = (x: number): number => ((x % 360) + 360) % 360;
  const a0 = deg(p0), a1 = deg(p1), a2 = deg(p2);
  return norm(a1 - a0) <= norm(a2 - a0)
    ? { center: cc.center, radius: cc.radius, startDeg: a0, endDeg: a2 }
    : { center: cc.center, radius: cc.radius, startDeg: a2, endDeg: a0 };
}

/** An arc's three grips, AutoCAD's: its two ends and the middle of its sweep. */
export function arcGripPoints(arc: { center: Vec2; radius: number; startDeg: number; endDeg: number }): { start: Vec2; mid: Vec2; end: Vec2 } {
  const norm = (x: number): number => ((x % 360) + 360) % 360;
  const at = (d: number): Vec2 => ({ x: arc.center.x + arc.radius * Math.cos((d * Math.PI) / 180), y: arc.center.y + arc.radius * Math.sin((d * Math.PI) / 180) });
  const sweep = norm(arc.endDeg - arc.startDeg) || 360;
  return { start: at(arc.startDeg), mid: at(arc.startDeg + sweep / 2), end: at(arc.endDeg) };
}

/**
 * Recover the circle / ellipse behind the point ring an `ellipse: true` polygon
 * stores. The ring is a tessellation, not a set of corners, so the shape's real
 * features have to be derived from it: the centroid is the centre, `points[0]`
 * the major-axis end and `points[n/4]` the minor-axis end — the order
 * `ellipsePoints` generates, which holds at any rotation because the ring is
 * rotated as a whole. Null for a ring too short to fit one.
 *
 * The ONE recovery: the renderer draws the ellipse from it and the object snap
 * offers its centre from it, so the marker can never sit off the drawn shape.
 */
export function ellipseFromRing(pts: readonly Vec2[]): { center: Vec2; rx: number; ry: number; rot: number } | null {
  const n = pts.length;
  if (n < 4) return null;
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  const p0 = pts[0], pq = pts[Math.round(n / 4) % n];
  return {
    center: { x: cx, y: cy },
    rx: distance(p0, { x: cx, y: cy }),
    ry: distance(pq, { x: cx, y: cy }),
    rot: Math.atan2(p0.y - cy, p0.x - cx),
  };
}

/** The four QUADRANT points of a fitted circle/ellipse: the tips of its own two
 *  axes, in the ellipse's frame (so a tilted one gives the tilted tips, not the
 *  world-axis extremes of its bounding box). These are the points on a curve CAD
 *  actually snaps to — its tessellated ring is not made of vertices, so without
 *  them a circle offers nothing but its centre. Order: +major, +minor, −major,
 *  −minor, i.e. counter-clockwise from the ring's own start point. */
export function ellipseQuadrants(fit: { center: Vec2; rx: number; ry: number; rot: number }): Vec2[] {
  const c = Math.cos(fit.rot), s = Math.sin(fit.rot);
  return [
    { x: fit.center.x + fit.rx * c, y: fit.center.y + fit.rx * s },
    { x: fit.center.x - fit.ry * s, y: fit.center.y + fit.ry * c },
    { x: fit.center.x - fit.rx * c, y: fit.center.y - fit.rx * s },
    { x: fit.center.x + fit.ry * s, y: fit.center.y - fit.ry * c },
  ];
}

/** How far a mitre may reach, in multiples of the offset, before a sharp corner
 *  is cut short: a wall's band and a connection's strands (a polygon offset by the
 *  Offset tool mitres exactly). */
export const BAND_MITRE = 4;

/** The ONE offset of a polyline: parallel to `pts` at signed distance `dist`
 *  (positive: to the left of its travel), every vertex on the mitre of its two
 *  offset sides — a closed ring's (`closed`: its last vertex joins its first, no
 *  point repeated) at every vertex, an open one's two ends square to their side. A
 *  mitre reaching further than `maxMitre` × `dist` (a sharp corner's spike) is cut
 *  to that length along the bisector; a side turning right back on itself takes
 *  the next side's normal; a side of no length takes its neighbour's. */
export function offsetPolyline(pts: Vec2[], dist: number, closed: boolean, maxMitre = Infinity): Vec2[] {
  const n = pts.length;
  if (n < 2) return pts.map((p) => ({ x: p.x, y: p.y }));
  const m = closed ? n : n - 1;
  const nrm: Array<Vec2 | null> = [];
  for (let i = 0; i < m; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    nrm.push(L < 1e-9 ? null : { x: -(b.y - a.y) / L, y: (b.x - a.x) / L });
  }
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const n1 = closed || i > 0 ? nrm[(i - 1 + m) % m] : null;
    const n2 = closed || i < n - 1 ? nrm[i % m] : null;
    let v: Vec2 = { x: 0, y: 0 };
    if (n1 && n2) {
      const bx = n1.x + n2.x, by = n1.y + n2.y, bl = Math.hypot(bx, by);
      if (bl < 1e-9) v = n2;
      else {
        const cos = (bx * n1.x + by * n1.y) / bl;
        const k = Math.min(maxMitre, 1 / cos) / bl;
        v = { x: bx * k, y: by * k };
      }
    } else v = n1 ?? n2 ?? v;
    out.push({ x: pts[i].x + v.x * dist, y: pts[i].y + v.y * dist });
  }
  return out;
}

/** The ONE rotation step, in degrees, for every rotate gesture (instance knob,
 *  shape / cloud / underlay knobs, a floor drain, a section view). */
export const ROTATE_STEP_DEG = 15;

/** A dragged rotation, PowerPoint's way: free by default, snapped to whole
 *  `ROTATE_STEP_DEG` steps while Shift is held. Degrees in, degrees out. */
export function snapRotationDeg(deg: number, shift: boolean): number {
  return shift ? Math.round(deg / ROTATE_STEP_DEG) * ROTATE_STEP_DEG : deg;
}

/** `snapRotationDeg` for an angle in radians. */
export function snapRotationRad(rad: number, shift: boolean): number {
  return shift ? (snapRotationDeg((rad * 180) / Math.PI, true) * Math.PI) / 180 : rad;
}

/** Whether a rubber-band marquee from `a` to `b` takes an object with these
 *  vertices — AutoCAD's rule, in any space: dragged left-to-right it is a WINDOW
 *  and takes only what lies wholly inside; right-to-left it is a CROSSING and takes
 *  anything with a vertex inside. The x order alone decides, so it holds on paper
 *  (Y-down) exactly as in the model (Y-up). */
export function marqueeTakes(a: Vec2, b: Vec2, verts: readonly Vec2[]): boolean {
  if (!verts.length) return false;
  const minx = Math.min(a.x, b.x), maxx = Math.max(a.x, b.x);
  const miny = Math.min(a.y, b.y), maxy = Math.max(a.y, b.y);
  const inside = (p: Vec2): boolean => p.x >= minx && p.x <= maxx && p.y >= miny && p.y <= maxy;
  return a.x <= b.x ? verts.every(inside) : verts.some(inside);
}

/** Reflection across the line through `a` and `b` (a mirror axis in any
 *  direction). Degenerate axis → identity. */
export function reflectAcross(a: Vec2, b: Vec2): (p: Vec2) => Vec2 {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  if (l2 < 1e-12) return (p) => ({ x: p.x, y: p.y });
  const c = (dx * dx - dy * dy) / l2, s = (2 * dx * dy) / l2; // cos 2θ, sin 2θ
  return (p) => {
    const px = p.x - a.x, py = p.y - a.y;
    return { x: a.x + c * px + s * py, y: a.y + s * px - c * py };
  };
}

/** The mirror axis' angle θ in degrees (for mirroring angles: φ → 2θ − φ). */
export function axisAngleDeg(a: Vec2, b: Vec2): number {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

/** A placed product's rotation after mirroring across an axis at `thetaDeg`,
 *  keeping its local Y (the wall side) and mirroring it in local X:
 *  R(θ)·Sx·R(−θ)·R(r) = R(2θ − r + 180)·Sy. Normalised to [0, 360). */
export function mirroredRotationDeg(rotation: number, thetaDeg: number): number {
  return (((2 * thetaDeg - rotation + 180) % 360) + 360) % 360;
}

/** EXPLODE: a polyline's segments, one two-point run each (a closed ring adds its
 *  closing segment). Zero-length segments are dropped. */
export function explodeSegments(points: readonly Vec2[], closed: boolean): Vec2[][] {
  const out: Vec2[][] = [];
  const n = points.length;
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const a = points[i], b = points[(i + 1) % n];
    if (Math.hypot(b.x - a.x, b.y - a.y) > 1e-9) out.push([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
  }
  return out;
}

/** JOIN: merge polylines whose ends meet (within `tol`) into as few chains as
 *  possible, reversing a piece where needed. Each result keeps the INDEX of the
 *  first piece that went into it (whose properties the joined line inherits), and
 *  a chain whose two ends meet is returned closed (its first point repeated last,
 *  as a closed line is stored). Input order decides ties; pieces never merge with
 *  themselves. */
export function joinChains(pieces: readonly (readonly Vec2[])[], tol: number): Array<{ points: Vec2[]; from: number; closed: boolean }> {
  const near = (p: Vec2, q: Vec2): boolean => Math.hypot(p.x - q.x, p.y - q.y) <= tol;
  const left = pieces.map((p, i) => ({ pts: p.map((v) => ({ x: v.x, y: v.y })), i })).filter((p) => p.pts.length >= 2);
  const out: Array<{ points: Vec2[]; from: number; closed: boolean }> = [];
  while (left.length) {
    const seed = left.shift()!;
    let chain = seed.pts;
    let grew = true;
    while (grew && !(chain.length > 2 && near(chain[0], chain[chain.length - 1]))) {
      grew = false;
      for (let k = 0; k < left.length; k++) {
        const p = left[k].pts, head = chain[0], tail = chain[chain.length - 1];
        let next: Vec2[] | null = null;
        if (near(tail, p[0])) next = [...chain, ...p.slice(1)];
        else if (near(tail, p[p.length - 1])) next = [...chain, ...p.slice(0, -1).reverse()];
        else if (near(head, p[p.length - 1])) next = [...p.slice(0, -1), ...chain];
        else if (near(head, p[0])) next = [...p.slice(1).reverse(), ...chain];
        if (next) { chain = next; left.splice(k, 1); grew = true; break; }
      }
    }
    const closed = chain.length > 3 && near(chain[0], chain[chain.length - 1]);
    if (closed) chain[chain.length - 1] = { ...chain[0] };
    out.push({ points: chain, from: seed.i, closed });
  }
  return out;
}

/** Cap the mitre length at this multiple of the offset. Beyond it (sharp
 *  corners) the join is beveled instead of forming a long tapering spike. */
export const RIBBON_MITER_LIMIT = 2.2;

/**
 * The offset point at node `p` on a one-sided ribbon that runs (prev→p→next),
 * offset by `sn` along the segment normal (dy, -dx). At an interior joint the two
 * adjacent segments' offset edges meet on the corner bisector; at a polyline end
 * the plain perpendicular offset of the sole segment is used. The mitre length is
 * clamped by RIBBON_MITER_LIMIT so a sharp bend beveys instead of shooting out a
 * long spike (which otherwise reads as the ribbon tapering near that vertex).
 * Keeps consecutive segments meeting cleanly — no overlaps, no empty corners.
 */
export function ribbonJoint(prev: Vec2 | null, p: Vec2, next: Vec2 | null, sn: number): Vec2 {
  const axes = (a: Vec2, b: Vec2): { nx: number; ny: number } => {
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { nx: (b.y - a.y) / l, ny: -(b.x - a.x) / l };
  };
  const inSeg = prev ? axes(prev, p) : null;   // segment entering p
  const outSeg = next ? axes(p, next) : null;  // segment leaving p
  if (!inSeg || !outSeg) {
    const s = (inSeg ?? outSeg)!;
    return { x: p.x + s.nx * sn, y: p.y + s.ny * sn };
  }
  // Corner bisector on the ribbon side; the mitre sits at sn / cos(half-angle)
  // along it. A near-180° fold has no bisector — fall back to a plain offset.
  let bx = inSeg.nx + outSeg.nx, by = inSeg.ny + outSeg.ny;
  const bl = Math.hypot(bx, by);
  if (bl < 1e-6) return { x: p.x + inSeg.nx * sn, y: p.y + inSeg.ny * sn };
  bx /= bl; by /= bl;
  const cos = bx * inSeg.nx + by * inSeg.ny; // bisector · normal = cos(half-angle)
  const d = sn / Math.max(Math.abs(cos), 1 / RIBBON_MITER_LIMIT);
  return { x: p.x + bx * d, y: p.y + by * d };
}

/** The whole perimeter of a node object's ribbon as it is drawn: the node line
 *  (its back edge) out, then the mitred front edge `depth` off it back. */
export function ribbonOutline(nodes: Vec2[], depth: number): Vec2[] {
  const front = nodes.map((p, i) => ribbonJoint(i > 0 ? nodes[i - 1] : null, p, i + 1 < nodes.length ? nodes[i + 1] : null, depth));
  return [...nodes, ...front.reverse()];
}

/** A node object's run as drawn, one mitred quad per segment `[a, b, fb, fa]`
 *  (the back edge on the node line, the front mitred with its neighbours). */
export function ribbonQuads(nodes: Vec2[], depth: number): Vec2[][] {
  const out: Vec2[][] = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    if (Math.hypot(b.x - a.x, b.y - a.y) < 1) continue;
    const fa = ribbonJoint(i > 0 ? nodes[i - 1] : null, a, b, depth), fb = ribbonJoint(a, b, i + 2 < nodes.length ? nodes[i + 2] : null, depth);
    out.push([a, b, fb, fa]);
  }
  return out;
}

/** Whether `p` lies on a node object's drawn run — inside one of its quads, or
 *  within `tol` of one — never on the square box past a bend's inner mitre, and
 *  whole where a tight run's outline crosses itself. */
export function ribbonContains(nodes: Vec2[], depth: number, p: Vec2, tol = 0): boolean {
  return ribbonQuads(nodes, depth).some((q) => {
    let inside = false;
    for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
      const a = q[i], b = q[j];
      if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
    }
    return inside || (tol > 0 && q.some((a, i) => distToSegment(p, a, q[(i + 1) % q.length]) <= tol));
  });
}

/** The length (mm) of an open polyline: a run's nodes end to end. */
export function polyBenchLength(nodes: readonly Vec2[]): number {
  let len = 0;
  for (let i = 0; i < nodes.length - 1; i++) len += Math.hypot(nodes[i + 1].x - nodes[i].x, nodes[i + 1].y - nodes[i].y);
  return len;
}

/** Is `p` inside the polygon `pts` (even-odd: a ray to +x crosses its edges an
 *  odd number of times)? A point on an edge may fall either way. The ONE test. */
export function pointInPolygon(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
